// First, update ImpersonationContext.js to properly restore from URL on page load

// src/contexts/ImpersonationContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Create the context
const ImpersonationContext = createContext(null);

// Provider component
export const ImpersonationProvider = ({ children }) => {
  const [impersonation, setImpersonation] = useState(null);
  const history = useHistory();
  const location = useLocation();

  // Try to restore impersonation from URL on component mount
  useEffect(() => {
    const impersonationMatch = location.pathname.match(/\/as\/(\d+)/);
    if (impersonationMatch) {
      // Store the employeeId from URL to be used by ProfileMenu
      const impersonatedId = parseInt(impersonationMatch[1], 10);
      sessionStorage.setItem('pendingImpersonation', impersonatedId.toString());
    }
  }, [location.pathname]);

  // Start impersonation
  const startImpersonation = (userInfo) => {
    setImpersonation(userInfo);

    // Store in session storage for persistence across refreshes
    sessionStorage.setItem('impersonationData', JSON.stringify({
      impersonatedId: userInfo.employeeId,
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || ''
    }));
    
    // Update URL to include impersonation info
    const currentPath = location.pathname;
    if (currentPath.includes('/as/')) {
      const newPath = currentPath.replace(/\/as\/\d+/, `/as/${userInfo.employeeId}`);
      history.replace(newPath);
    } else {
      history.replace(`${currentPath}/as/${userInfo.employeeId}`);
    }
  };
  
  // Stop impersonation
  const stopImpersonation = () => {
    setImpersonation(null);
    
    // Clear from session storage
    sessionStorage.removeItem('impersonationData');
    sessionStorage.removeItem('pendingImpersonation');
    
    // Update URL to remove impersonation info
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/\/as\/\d+(?:\/|$)/, '');
    history.replace(newPath);
  };

  // Check for stored impersonation data on initial load
  useEffect(() => {
    const storedData = sessionStorage.getItem('impersonationData');
    if (storedData) {
      setImpersonation(JSON.parse(storedData));
    }
  }, []);

  return (
    <ImpersonationContext.Provider 
      value={{ 
        impersonation, 
        startImpersonation, 
        stopImpersonation,
        isImpersonating: !!impersonation
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

// Custom hook for using the impersonation context
export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  
  return context;
};

export default ImpersonationContext;